/**
 * 환산계수 동기화 서비스
 *
 * Backend의 환산계수 JSON 파일을 동기화하고 Unit.js에 주입합니다.
 * - 버전 비교를 통한 효율적인 동기화
 * - TTL 기반 자동 갱신
 * - WebSocket을 통한 실시간 업데이트
 */

import backEndApi from "../api/backEndApi";

// xelib에서 Unit 가져오기 (동적 import)
let Unit;
try {
    const xelib = require("xelib");
    Unit = xelib.PointBoxV2.Unit;
} catch (e) {
    console.error("[UnitConversionService] xelib 로드 실패:", e);
    // Fallback: Unit이 전역에 있을 수도 있음
    Unit = global.xe?.Unit || window.Unit;
}

class UnitConversionService {
    constructor() {
        this.currentVersion = null;
        this.lastSyncTime = null;
        this.syncInterval = null;
        this.ttl = 5 * 60 * 1000; // 5분 (밀리초)
        this.syncDebounceTimer = null;
        this.debounceDelay = 300; // 300ms Debounce 지연 시간
        this.pendingSync = null; // 진행 중인 동기화 Promise (재사용용)
    }

    /**
     * 서비스 초기화
     * @param {string} serverVersion - 서버의 환산계수 버전 (로그인 응답에서 받음)
     */
    async initialize(serverVersion) {
        try {
            // 서버 버전이 없으면 초기 동기화 수행
            if (!serverVersion) {
                console.warn("[UnitConversionService] 서버 버전이 없습니다. 초기 동기화를 수행합니다.");
                await this.sync();
                return;
            }

            // 로컬 스토리지에서 버전 확인
            const localVersion = localStorage.getItem("unitConversionVersion");
            const localData = localStorage.getItem("unitConversionData");

            // 버전이 다르거나 데이터가 없으면 동기화
            if (localVersion !== serverVersion || !localData) {
                await this.sync();
            } else {
                // 로컬 데이터가 있으면 먼저 로드
                this.loadFromLocalStorage();

                // 백그라운드에서 버전 확인 (조용히 업데이트)
                this.checkVersionAsync(serverVersion);
            }

            // TTL 기반 자동 갱신 시작
            this.startAutoSync();
        } catch (error) {
            console.error("[UnitConversionService] 초기화 실패:", error);
        }
    }

    /**
     * 환산계수 동기화 (서버에서 JSON 파일 다운로드)
     * 개선된 Debounce 방식: 짧은 시간 내 여러 호출을 하나로 통합하고, 진행 중인 동기화는 재사용
     */
    async sync() {
        // 이미 동기화가 진행 중이면 그 Promise를 재사용
        if (this.pendingSync) {
            console.log("[UnitConversionService] 동기화가 이미 진행 중입니다. 기존 Promise 재사용.");
            return this.pendingSync;
        }

        // Debounce: 짧은 시간 내 여러 호출을 하나로 통합
        return new Promise((resolve, reject) => {
            // 이전 타이머가 있으면 취소
            if (this.syncDebounceTimer) {
                clearTimeout(this.syncDebounceTimer);
            }

            // 새 타이머 설정
            this.syncDebounceTimer = setTimeout(async () => {
                this.syncDebounceTimer = null;

                // 실제 동기화 수행
                this.pendingSync = this._doSync();

                try {
                    const result = await this.pendingSync;
                    resolve(result);
                } catch (error) {
                    reject(error);
                } finally {
                    this.pendingSync = null;
                }
            }, this.debounceDelay);
        });
    }

    /**
     * 실제 동기화 수행 (내부 메서드)
     */
    async _doSync() {
        try {
            // 현재 환산계수 조회 (버전 정보 포함)
            const response = await backEndApi.unitConversion.getCurrentConversions();

            if (!response || !response.data) {
                throw new Error("환산계수 조회 실패");
            }

            // 응답 형식: { version, conversion } (Unit.conversion 구조)
            const data = response.data;
            const version = data.version;
            const conversion = data.conversion || {};

            // Unit.js에 데이터 주입 (Unit.conversion 및 Unit.conversionVersion 직접 업데이트)
            if (Unit) {
                if (Unit.conversion) {
                    // Unit.conversion 및 Unit.conversionVersion 직접 업데이트
                    Object.assign(Unit.conversion, conversion);
                    if (Unit.hasOwnProperty("conversionVersion")) {
                        Unit.conversionVersion = version;
                    }
                    console.log("[UnitConversionService] Unit.conversion 및 Unit.conversionVersion 업데이트 완료");
                } else {
                    console.error("[UnitConversionService] Unit.conversion이 존재하지 않습니다.");
                }
            } else {
                console.error("[UnitConversionService] Unit 객체를 사용할 수 없습니다.");
                // Unit이 없으면 동기화는 성공했지만 환산계수 변환이 동작하지 않을 수 있음
                // 하지만 데이터는 로컬 스토리지에 저장되므로 나중에 Unit이 로드되면 사용 가능
            }

            // 로컬 스토리지에 저장
            localStorage.setItem("unitConversionVersion", version);
            localStorage.setItem("unitConversionData", JSON.stringify({ version, conversion }));
            localStorage.setItem("unitConversionLastSync", new Date().toISOString());

            this.currentVersion = version;
            this.lastSyncTime = new Date();

            return { version, conversion };
        } catch (error) {
            console.error("[UnitConversionService] 동기화 실패:", error);
            // 실패 시 로컬 데이터라도 로드 시도
            this.loadFromLocalStorage();
            throw error;
        }
    }

    /**
     * 로컬 스토리지에서 데이터 로드
     */
    loadFromLocalStorage() {
        try {
            const localData = localStorage.getItem("unitConversionData");
            if (localData) {
                const data = JSON.parse(localData);
                const conversion = data.conversion || {};

                // Unit.conversion 및 Unit.conversionVersion 직접 업데이트
                if (Unit && Unit.conversion) {
                    Object.assign(Unit.conversion, conversion);
                    if (Unit.hasOwnProperty("conversionVersion") && data.version) {
                        Unit.conversionVersion = data.version;
                    }
                }

                this.currentVersion = data.version;
                const lastSync = localStorage.getItem("unitConversionLastSync");
                if (lastSync) {
                    this.lastSyncTime = new Date(lastSync);
                }
            }
        } catch (error) {
            console.error("[UnitConversionService] 로컬 데이터 로드 실패:", error);
        }
    }

    /**
     * 비동기 버전 확인 (백그라운드에서 조용히 확인)
     */
    async checkVersionAsync(serverVersion) {
        try {
            // 서버 버전과 로컬 버전 비교
            const localVersion = localStorage.getItem("unitConversionVersion");
            if (localVersion !== serverVersion) {
                console.log("[UnitConversionService] 버전 불일치 감지, 백그라운드 동기화 수행.");
                await this.sync();
            }
        } catch (error) {
            console.error("[UnitConversionService] 버전 확인 실패:", error);
        }
    }

    /**
     * TTL 기반 자동 갱신 시작
     */
    startAutoSync() {
        // 기존 인터벌 제거
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
        }

        // TTL마다 동기화 수행
        this.syncInterval = setInterval(() => {
            const now = new Date();
            if (!this.lastSyncTime || now - this.lastSyncTime >= this.ttl) {
                this.sync();
            }
        }, this.ttl);
    }

    /**
     * 자동 갱신 중지
     */
    stopAutoSync() {
        if (this.syncInterval) {
            clearInterval(this.syncInterval);
            this.syncInterval = null;
        }
    }

    /**
     * WebSocket 업데이트 처리
     * @param {Object|string} conversionDataOrVersion - 새로운 환산계수 데이터 또는 버전 문자열
     *   - Object인 경우: { version, conversions }
     *   - string인 경우: 버전 문자열 (하위 호환성)
     */
    async handleWebSocketUpdate(conversionDataOrVersion) {
        try {
            // 하위 호환성: 문자열인 경우 (버전만 전달된 경우)
            if (typeof conversionDataOrVersion === "string") {
                const newVersion = conversionDataOrVersion;
                console.log("[UnitConversionService] WebSocket 업데이트 수신 (버전만), 새 버전:", newVersion);

                // 버전이 다르면 즉시 동기화
                if (this.currentVersion !== newVersion) {
                    await this.sync();
                }
                return;
            }

            // 새로운 방식: conversionData 객체 전달
            const conversionData = conversionDataOrVersion;
            if (!conversionData || !conversionData.version) {
                console.warn("[UnitConversionService] WebSocket 업데이트 데이터가 올바르지 않습니다:", conversionData);
                // 데이터가 없으면 동기화로 폴백
                await this.sync();
                return;
            }

            const newVersion = conversionData.version;
            const conversion = conversionData.conversion || {};

            console.log("[UnitConversionService] WebSocket 업데이트 수신, 새 버전:", newVersion);

            // 버전이 같으면 업데이트 불필요
            if (this.currentVersion === newVersion) {
                console.log("[UnitConversionService] 버전이 동일하여 업데이트를 건너뜁니다.");
                return;
            }

            // Unit.js에 데이터 직접 주입 (Unit.conversion 및 Unit.conversionVersion 직접 업데이트)
            if (Unit && Unit.conversion) {
                Object.assign(Unit.conversion, conversion);
                if (Unit.hasOwnProperty("conversionVersion")) {
                    Unit.conversionVersion = newVersion;
                }
                console.log("[UnitConversionService] Unit.conversion 및 Unit.conversionVersion 업데이트 완료");
            } else {
                console.error("[UnitConversionService] Unit.conversion을 사용할 수 없습니다. 동기화로 폴백합니다.");
                await this.sync();
                return;
            }

            // 로컬 스토리지에 저장
            localStorage.setItem("unitConversionVersion", newVersion);
            localStorage.setItem("unitConversionData", JSON.stringify({ version: newVersion, conversion }));
            localStorage.setItem("unitConversionLastSync", new Date().toISOString());

            this.currentVersion = newVersion;
            this.lastSyncTime = new Date();

            console.log("[UnitConversionService] WebSocket 업데이트 완료, 버전:", newVersion);
        } catch (error) {
            console.error("[UnitConversionService] WebSocket 업데이트 처리 실패:", error);
            // 실패 시 동기화로 폴백
            try {
                await this.sync();
            } catch (syncError) {
                console.error("[UnitConversionService] 동기화 폴백 실패:", syncError);
            }
        }
    }

    /**
     * 현재 버전 조회
     */
    getVersion() {
        return this.currentVersion || localStorage.getItem("unitConversionVersion");
    }

    /**
     * 수동 동기화 (사용자 요청)
     */
    async manualSync() {
        console.log("[UnitConversionService] 수동 동기화 요청");
        await this.sync();
    }
}

// 싱글톤 인스턴스 생성 및 export
const unitConversionService = new UnitConversionService();

export default unitConversionService;
